import { some } from 'lodash'
import Vue from 'vue'
import { localforageFavorites } from '@/localforage'
import i18n from '@/i18n'

function posToPosition(pos) {
  const lng = pos.substring(0, pos.lastIndexOf(' '))
  const lat = pos.substring(pos.lastIndexOf(' ') + 1, pos.length)
  return {
    lat: Number(lat),
    lng: Number(lng)
  }
}
function positionToCoords(position) {
  return `${position.lat}, ${position.lng}`
}

function getCategories(array) {
  // check if ks categories available
  let results = array.map(getCategoryNameByCategoryUri).filter(Boolean)
  // prevent double entries
  return [...new Set(results)].sort()
}

function getCategoryNameByCategoryUri({ uri }) {
  const catId = 'categories.' + uri
  return i18n.te(catId)
    ? i18n.t(catId)
    : false
}

function getTagsByOptIds(array, hasLivingImages) {
  array = array.map(getTagNameByTagId).filter(Boolean)
  if (hasLivingImages) array.push('Living Image')
  return array.sort()
}

function getTagNameByTagId({ id }) {
  const tagId = 'tags.' + id
  return i18n.te(tagId)
    ? i18n.t(tagId)
    : false
}

function getHolidaysByIds(obj) {
  return Object.values(obj)
    .map(getHolidayNameById)
    .filter(Boolean)
    .sort()
}

function getHolidayNameById({ id }) {
  const holidayId = 'details.holidays.' + id
  return i18n.te(holidayId)
    ? i18n.t(holidayId)
    : false
}

// Living Image Object for testing purposes
class LivingImage {
  constructor(id, title, markerImageURL, markerPatternURL, videoURL, patternRatio = 0.9) {
    this.id = id
    this.title = title
    this.image = markerImageURL
    this.pattern = markerPatternURL
    this.video = videoURL
    this.patternRatio = patternRatio
  }
}

class InstitutionService {
  constructor(digiCultUrl) {
    this.digiCultUrl = digiCultUrl || 'https://kultursphaere.sh/corsproxy.php?url=http://xtree-actor-api.digicult-verbund.de'
  }

  async fetchInstitutions() {
    const locale = i18n.locale
    const response = await Vue.http.get(`${this.digiCultUrl}/getRepositoryList?portalURI=http://digicult.vocnet.org/portal/p0287&count=9999&lang=${locale}`)
    const data = await response.json()

    const favorites = await localforageFavorites.keys()

    return Promise.all(data.Actor.map(async institution => {
      // choose shortname as name if exists
      if (institution.nameAddition) institution.name = institution.nameAddition

      // set images (g - TODO: both needed?)
      institution.imageList = {}
      if (institution.resource && institution.resource.resourceRepresentation) {
        institution.imageList.thumbnail = institution.resource.resourceRepresentation.link
        institution.imageList.preview = institution.imageList.thumbnail.replace('thumbnail', 'preview_image')
        institution.imageList.provided = institution.imageList.thumbnail.replace('thumbnail', 'provided_image')
      }

      // create position object and coords string
      if (institution.pos) {
        institution.position = posToPosition(institution.pos)
        institution.coords = positionToCoords(institution.position)
      }

      /* TEST FOR LIVING IMAGES  */
      if (institution.id === 'act0002156' || institution.id === 'act001653') { // assign Living Images to Schauspielhaus and SHLB
        institution.hasLivingImages = true
      } else institution.hasLivingImages = false

      // get categories
      if (institution.classification) {
        const categoryUris = institution.classification.filter(classification => classification.type === 'tourismustyp')
        const categories = await getCategories(categoryUris)
        if (categories.length > 0) institution.categories = categories
        else console.log('No category: ', institution)
      }

      // get tag names
      if (institution.icon) {
        let tags = getTagsByOptIds(institution.icon, institution.hasLivingImages)
        if (tags.length > 0) institution.tags = tags
      }

      // Set favorite state
      institution.isFavorite = some(favorites, id => institution.id === id)

      return institution
    }))
  }

  async fetchInstitution(id, locale) {
    const response = await Vue.http.get(`${this.digiCultUrl}/getRepositoryItem?id=${id}&lang=${locale}`)
    const data = await response.json()

    const favorites = await localforageFavorites.keys()

    let institution = data.Actor

    console.log('Details geholt: ', institution)

    // set preferred name as value for name property (identifier: name.role = preferred)
    institution.name = institution.name.find(item => item.role === 'preferred')
    if (institution.name.nameAddition) {
      institution.name = institution.name.nameAddition
    } else if (institution.name.label) {
      institution.name = institution.name.label
    } else {
      institution.name = 'Unbekannter Name'
    }

    if (institution.description) {
      // set claim property (identifier: description.id = info028)
      institution.claim = institution.description.find(item => item.id === 'info028')
      institution.claim = institution.claim ? institution.claim.noteValue[0] : ''

      // set description property (identifier: description.id = info029)
      let ksDescription = institution.description.find(item => item.id === 'info029')
      ksDescription = ksDescription ? ksDescription.noteValue[0] : null
      // if our description is yet missing fill in the digicult description for development purposes
      if (ksDescription == null) {
        institution.description = institution.description.find(item => item.id === 'info001')
        institution.description = institution.description ? '[digiCULT] ' + institution.description.noteValue[0] : 'Es ist noch keine Beschreibung vorhanden.'
      } else {
        institution.description = ksDescription
      }
    }

    // set address property (identifier: address.type = visitor)
    if (institution.address) institution.address = institution.address.find(item => item.type === 'visitor')

    if (institution.communication) {
      // set tel property (identifier: communication.id = com0022)
      institution.tel = institution.communication.find(item => item.id === 'com0022')
      institution.tel = institution.tel ? institution.tel.item : null

      // set email property (identifier: id = communication.com0024)
      institution.email = institution.communication.find(item => item.id === 'com0024')
      institution.email = institution.email ? institution.email.item : null
    }

    if (institution.webResource) {
      // set website property (identifier: webResource.source_id = webpage)
      institution.website = institution.webResource.find(item => item.source_id === 'webpage')
      if (!institution.website) institution.website = false

      // get social media (identifier: webResource.source_id = socialMedia)
      const socialMedia = institution.webResource.filter(item => item.source_id === 'socialMedia' && item.label)
      if (socialMedia.length > 0) {
        // set facebook property (identifier: label = Facebook)
        institution.facebook = socialMedia.find(item => item.label.toLowerCase() === 'facebook')
        institution.facebook = institution.facebook ? institution.facebook.identifier : null

        // set twitter property (identifier: label = Twitter)
        institution.twitter = socialMedia.find(item => item.label.toLowerCase() === 'twitter')
        institution.twitter = institution.twitter ? institution.twitter.identifier : null

        // set youtube property (identifier: label = YouTube)
        institution.youtube = socialMedia.find(item => item.label.toLowerCase() === 'youtube')
        institution.youtube = institution.youtube ? institution.youtube.identifier : null

        // set instagram property (identifier: label = Instagram)
        institution.instagram = socialMedia.find(item => item.label.toLowerCase() === 'instagram')
        institution.instagram = institution.instagram ? institution.instagram.identifier : null
      }

      // get digital services
      const digitalServices = {
        'c00023': '360',
        'c00052': 'audioguide',
        'c00020': 'augmentedReality',
        'c00024': 'chatbot',
        'c00025': 'multimedia',
        'c00022': 'video',
        'c00021': 'virtualReality'
      }
      institution.digitalServices = institution.webResource
        .filter(item => item.source_id in digitalServices && item.label && item.identifier)
        .map(item => {
          item.icon = digitalServices[item.source_id]
          return item
        })
    }

    // get apps
    institution.apps = {
      android: [],
      ios: []
    }
    if (institution.mapping) {
      institution.apps.ios = institution.mapping.filter(item => item.source_id === 'app_store_ios')
      institution.apps.android = institution.mapping.filter(item => item.source_id === 'app_store_android')
    }

    // if no website in database, check if isil available and set museen-nord page as website
    if (!institution.website) {
      if (institution.mapping) {
        const isil = institution.mapping.find((item) => item.source_id === 'ISIL')
        if (isil) {
          institution.website = {
            identifier: 'http://www.museen-nord.de/museum/' + isil.identifier,
            label: 'museen-nord.de'
          }
        }
      }
    }

    // set images (TODO: need kultursphaere identifier!)
    if (institution.resource) {
      institution.image = institution.resource.length > 0
        ? institution.resource[0]
        : []
    }

    // create position object and coords string
    if (institution.pos) {
      institution.position = posToPosition(institution.pos)
      institution.coords = positionToCoords(institution.position)
    }

    // get opening hours
    if (institution.openingTimes && institution.openingTimes.length > 0) {
      institution.openingTimes = institution.openingTimes[0]
    }

    // filter our used holidays
    if (institution.closedHolidays) {
      let holidays = getHolidaysByIds(institution.closedHolidays)
      if (holidays.length > 0) institution.closedHolidays = holidays
    }

    /* TEST FOR LIVING IMAGES  */
    institution.livingImages = []
    const baseUrl = 'https://www.kultursphaere.sh/living-images/'
    if (institution.id === 'act0002156') { // assign Living Images to Schauspielhaus
      institution.hasLivingImages = true
      institution.livingImages.push(
        new LivingImage(
          '47E53F2F010CCECE2B2EE9032433DCBB',
          'Die Räuber',
          baseUrl + 'marker_marko-gebbert.jpg',
          baseUrl + 'pattern_marko-gebbert.patt',
          baseUrl + 'video_marko-gebbert.mp4'
        ),
        new LivingImage(
          'FD0A726090A63CF276A9DD45886F20F4',
          'Drei Mal Leben',
          baseUrl + 'marker_yvonne-ruprecht.jpg',
          baseUrl + 'pattern_yvonne-ruprecht.patt',
          baseUrl + 'video_yvonne-ruprecht.mp4'
        )
      )
    } else if (institution.id === 'act001653') {
      institution.hasLivingImages = true
      institution.livingImages.push(
        new LivingImage(
          '3d3dd3acca43b3fb4adca942235df783',
          'Gemälde von Monika-Maria Dotzer',
          baseUrl + 'marker_shlb-dotzer.jpg',
          baseUrl + 'pattern_shlb-dotzer.patt',
          baseUrl + 'video_shlb-dotzer.mp4'
        ),
        new LivingImage(
          '152857c7309e41f0bd49ed14ef900de6',
          'Willkommensgruß',
          baseUrl + 'marker_laetzel-willkommen.jpg',
          baseUrl + 'pattern_laetzel-willkommen.patt',
          baseUrl + 'video_laetzel-willkommen.mp4'
        )
      )
    } else institution.hasLivingImages = false

    // get categories
    if (institution.classification) {
      const categoryUris = institution.classification.filter(classification => classification.type === 'tourismustyp')
      const categories = await getCategories(categoryUris)
      if (categories.length > 0) institution.categories = categories
    }

    // get tag names
    if (institution.icon) {
      let tags = getTagsByOptIds(institution.icon, institution.hasLivingImages)
      if (tags.length > 0) institution.tags = tags
    }

    // Set favorite state
    institution.isFavorite = some(favorites, id => institution.id === id)

    return institution
  }
}

export default new InstitutionService()
